import classNames from 'classnames';
import { type ReactNode, useCallback, useState } from 'react';
import Typography from '@alltrails/core/components/Typography';
import IconButton from '@alltrails/denali/components/IconButton';
import Close from '@alltrails/denali/icons/Close';
import ThumbsDown from '@alltrails/denali/icons/ThumbsDown';
import ThumbsDownFilled from '@alltrails/denali/icons/ThumbsDownFilled';
import ThumbsUp from '@alltrails/denali/icons/ThumbsUp';
import ThumbsUpFilled from '@alltrails/denali/icons/ThumbsUpFilled';
import FeedbackConfirmationDialog from '../FeedbackConfirmationDialog';
import FeedbackModal from '../FeedbackModal';
import styles from './FeedbackCard.module.scss';

const CONFIRMATION_DURATION_MS = 3000;

export type FeedbackCardCopy = {
  cancelButton: ReactNode;
  dismissLabel: string;
  helpfulLabel: string;
  modalTitle: string;
  notHelpfulLabel: string;
  submitButton: ReactNode;
  subtitle: ReactNode;
  textAreaLabel: string;
  textAreaPlaceholder: string;
  title: ReactNode;
};

export type FeedbackCardSubmission = {
  feedback: string;
  helpfulRating: boolean;
};

type FeedbackCardProps = {
  className?: string;
  copy: FeedbackCardCopy;
  onClose?: () => void;
  /** Called as soon as thumbs up or down is pressed, before the modal opens, whether or not feedback is submitted. */
  onRate?: (helpfulRating: boolean) => void;
  onSubmit: (submission: FeedbackCardSubmission) => void;
  /** `compact` shows just the thumbs, with no title, subtitle or dismiss, and keeps them after feedback is submitted. */
  variant?: 'card' | 'compact';
};

const FeedbackCard = ({ className, copy, onClose, onRate, onSubmit, variant = 'card' }: FeedbackCardProps) => {
  const isCompact = variant === 'compact';
  // The most recent thumbs pressed. It stays filled after the modal closes, until another thumbs is pressed.
  const [isHelpful, setIsHelpful] = useState<boolean>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCardVisible, setIsCardVisible] = useState(true);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const openModalWithSentiment = useCallback(
    (helpfulRating: boolean) => {
      setIsHelpful(helpfulRating);
      onRate?.(helpfulRating);
      setIsModalOpen(true);
    },
    [onRate]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onDismiss = useCallback(() => {
    setIsCardVisible(false);
    onClose?.();
  }, [onClose]);

  const submitFeedback = useCallback(
    (feedback: string) => {
      onSubmit({ feedback, helpfulRating: Boolean(isHelpful) });
      setIsModalOpen(false);
      setIsCardVisible(isCompact);
      setIsConfirmationOpen(true);
    },
    [isCompact, isHelpful, onSubmit]
  );

  const onConfirmationClose = useCallback(() => {
    setIsConfirmationOpen(false);
    onClose?.();
  }, [onClose]);

  const thumbsSize = isCompact ? 'xs' : 'sm';
  const thumbsVariant = isCompact ? 'ghost' : 'secondary';
  const thumbs = (
    <>
      <IconButton
        ariaPressed={isHelpful === true}
        icon={{ Component: isHelpful === true ? ThumbsUpFilled : ThumbsUp }}
        onClick={() => openModalWithSentiment(true /* helpfulRating */)}
        size={thumbsSize}
        title={copy.helpfulLabel}
        variant={thumbsVariant}
      />
      <IconButton
        ariaPressed={isHelpful === false}
        icon={{ Component: isHelpful === false ? ThumbsDownFilled : ThumbsDown }}
        onClick={() => openModalWithSentiment(false /* helpfulRating */)}
        size={thumbsSize}
        title={copy.notHelpfulLabel}
        variant={thumbsVariant}
      />
    </>
  );

  return (
    <>
      {isCardVisible &&
        (isCompact ? (
          <div className={classNames(styles.thumbs, className)}>{thumbs}</div>
        ) : (
          <div className={classNames(styles.card, className)}>
            <div className={styles.header}>
              <div className={styles.copy}>
                <Typography variant="text200bold">{copy.title}</Typography>
                <Typography color="secondary" variant="text100">
                  {copy.subtitle}
                </Typography>
              </div>
              <IconButton
                className={styles.close}
                icon={{ Component: Close }}
                onClick={onDismiss}
                size="sm"
                title={copy.dismissLabel}
                variant="ghost"
              />
            </div>
            <div className={styles.thumbs}>{thumbs}</div>
          </div>
        ))}
      <FeedbackModal
        copy={{
          cancelButton: copy.cancelButton,
          submitButton: copy.submitButton,
          textAreaLabel: copy.textAreaLabel,
          textAreaPlaceholder: copy.textAreaPlaceholder,
          title: copy.modalTitle
        }}
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={submitFeedback}
      />
      <FeedbackConfirmationDialog durationMs={CONFIRMATION_DURATION_MS} isOpen={isConfirmationOpen} onRequestClose={onConfirmationClose} />
    </>
  );
};

export default FeedbackCard;
